// External Dependencies
import { alpha } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import styled, { useTheme } from 'styled-components';
import type { ReactElement } from 'react';
import { CardProps } from '@mui/material/Card';

// Local Dependencies
import EnhancedCard from './EnhancedCard';
import EnhancedCardContent from './EnhancedCardContent';
import type { StripeBlueColor } from '../../types/theme.d';

// Local Typings
export interface DetailCardProps {
  avatarAriaLabel: string;
  avatarBackgroundColor?: keyof StripeBlueColor;
  avatarIcon: ReactElement;
  content: ReactElement;
  subheader?: ReactElement | string;
  sx?: Omit<CardProps['sx'], 'borderRadius'>;
  title: string;
}

// Local Variables
const StyledEnhancedCard = styled(EnhancedCard)(({ theme }) => ({
  '.detail-card-header': {
    alignItems: 'flex-start',
    backgroundColor: alpha(theme.palette.accentBlue, 0.1),
  },
  '.title': {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: 500,
  },
}));

// Component Definition
const DetailCard = ({
  avatarAriaLabel,
  avatarBackgroundColor,
  avatarIcon,
  content,
  subheader,
  sx,
  title,
}: DetailCardProps): JSX.Element => {
  const theme = useTheme();

  const backgroundColor = theme.palette.stripeBlue[avatarBackgroundColor || 500];

  return (
    <StyledEnhancedCard sx={{ borderRadius: 2.5, ...sx }}>
      <CardHeader
        avatar={(
          <Avatar
            aria-label={avatarAriaLabel}
            sx={{ backgroundColor }}
          >
            {avatarIcon}
          </Avatar>
        )}
        className="detail-card-header"
        classes={{ title: 'title' }}
        subheader={subheader}
        title={title}
      />

      <Divider />

      <EnhancedCardContent>
        {content}
      </EnhancedCardContent>
    </StyledEnhancedCard>
  );
};

export default DetailCard;
